<template>
  <div class="login-page">
    <div class="login-container">
      <n-card title="Login" style="max-width: 400px; margin: 0 auto;">
        <n-form :model="formData" :rules="rules" ref="formRef">
          <n-form-item label="Email" path="email">
            <n-input v-model:value="formData.email" placeholder="Enter your email" />
          </n-form-item>
          <n-form-item label="Password" path="password">
            <n-input 
              v-model:value="formData.password" 
              type="password" 
              placeholder="Enter your password"
              @keyup.enter="handleLogin"
            />
          </n-form-item>
          <n-form-item>
            <n-button 
              type="primary" 
              @click="handleLogin" 
              :loading="loading"
              block
            >
              Login
            </n-button>
          </n-form-item>
        </n-form>
        <n-divider />
        <div class="auth-footer">
          <p>Don't have an account?</p>
          <n-button @click="$router.push('/register')" type="primary" ghost>
            Register
          </n-button>
        </div>
      </n-card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useMessage } from 'naive-ui'
import { useRoute } from 'vue-router'
import { apiClient, type LoginRequest } from '@/services/api'

const message = useMessage()
const route = useRoute()
const formRef = ref()
const loading = ref(false)

const formData = reactive<LoginRequest>({
  email: '',
  password: '',
})

const rules = {
  email: {
    required: true,
    message: 'Please enter your email',
    trigger: 'blur',
  },
  password: {
    required: true,
    message: 'Please enter your password',
    trigger: 'blur',
  },
}

const handleLogin = async () => {
  try {
    await formRef.value?.validate()
    loading.value = true
    
    await apiClient.login(formData)
    message.success('Login successful!')
    
    // Redirect to intended destination or home
    const redirect = (route.query.redirect as string) || '/'
    // Force a page reload to reinitialize the user state
    // This ensures cookies are properly read and user state is updated
    window.location.href = redirect
  } catch (error) {
    message.error('Login failed. Please check your credentials.')
    console.error(error)
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.login-page {
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--body-color);
}

.login-container {
  width: 100%;
  max-width: 400px;
  padding: 24px;
}

.auth-footer {
  text-align: center;
}

.auth-footer p {
  margin: 0 0 12px 0;
  color: #666;
}

/* Remove black bars on input sides */
:deep(.n-form-item) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

:deep(.n-input-wrapper) {
  border-left: none !important;
  border-right: none !important;
  background: transparent !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

:deep(.n-input__input) {
  border-left: none !important;
  border-right: none !important;
  background: transparent !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

:deep(.n-input__input-el) {
  border-left: none !important;
  border-right: none !important;
  background: transparent !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

:deep(.n-input) {
  border-left: none !important;
  border-right: none !important;
}
</style>
